import React, {useEffect, useRef, useState} from 'react'
import {FaChevronLeft, FaChevronRight} from 'react-icons/fa'

interface ScrollWrapperProps {
  children: React.ReactNode
  className?: string
}

const ScrollWrapper: React.FC<ScrollWrapperProps> = ({children, className = ''}) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [isScrollable, setIsScrollable] = useState(false)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollState = () => {
    const el = scrollRef.current
    if (!el) return

    const {scrollLeft, scrollWidth, clientWidth} = el

    setIsScrollable(scrollWidth > clientWidth)
    setCanScrollLeft(scrollLeft > 0)
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth)
  }

  useEffect(() => {
    updateScrollState()
    window.addEventListener('resize', updateScrollState)
    return () => window.removeEventListener('resize', updateScrollState)
  }, [children])

  // also update on user scroll
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.addEventListener('scroll', updateScrollState, {passive: true})
    return () => el.removeEventListener('scroll', updateScrollState)
  }, [])

  const scrollBy = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return
    const delta = direction === 'left' ? -300 : 300
    scrollRef.current.scrollBy({left: delta, behavior: 'smooth'})
  }

  return (
    <div
      className={`scroll-wrapper position-relative ${className}`}
      onMouseEnter={updateScrollState}
    >
      {isScrollable && canScrollLeft && (
        <div
          className='scroll-button scroll-button-left position-absolute top-0 start-0 d-flex align-items-center rounded-start left-button-layout'
          onClick={() => scrollBy('left')}
        >
          <FaChevronLeft className='ps-2 fs-3' />
        </div>
      )}
      {isScrollable && canScrollRight && (
        <div
          className='scroll-button scroll-button-right position-absolute top-0 end-0 d-flex align-items-center justify-content-end rounded-end right-button-layout'
          onClick={() => scrollBy('right')}
        >
          <FaChevronRight className='pe-2 fs-3' />
        </div>
      )}

      <div
        className='scroll-container d-flex scroll-content'
        ref={scrollRef}
        style={{scrollBehavior: 'smooth'}}
      >
        {children}
      </div>
    </div>
  )
}

export default ScrollWrapper
